import { createSlice } from '@reduxjs/toolkit';
import { config } from '../../config';
import { SearchSliceState } from '../../types';
import { getUrlPageNumber } from '../../utils';

// Number of columns in grid view
export const gridColumnNumber = 5;

// Read initial page number
let initialPageNumber = 1;
const currentPageNumber = getUrlPageNumber();
if (currentPageNumber != null) {
  initialPageNumber = Number(currentPageNumber) ?? 1;
}

const isInitSearching = true;

export const initialSearchState: SearchSliceState = {
  results: [],
  pagination: {
    currentPage: initialPageNumber,
    totalPages: 1,
    perPage: config.search.resultsPerPage,
    totalResults: 0,
  },
  contentBlocks: [],
  searchOptions: {
    query: '',
    page: 1,
    action: undefined,
    sortBy: undefined,
    residentialCommercial: null,
  },
  isSearching: isInitSearching,
  isSearchQueryRelaxed: false,
  isSearchRedirect: false,
  isFetching: false,
  isGridMode: false,
  xRequestId: undefined,
  xSessionId: undefined,
  contentBlockXRequestId: undefined,
  campaignBanners: [],
  materialsInQueue: [],
  productWithFilePreviewInQueue: undefined,
  collectionHeader: undefined,
  selectedVariants: {},
  stripMaterials: [],
  isStripMaterialsLoading: false,
};

const searchSlice = createSlice({
  name: 'search',
  initialState: initialSearchState,
  reducers: {
    setResults(
      state,
      {
        payload = {
          results: [],
          contentBlocks: [],
        },
      },
    ) {
      state.results = payload.results || [];
      if (Array.isArray(payload.contentBlocks)) {
        state.contentBlocks = payload.contentBlocks || [];
      }
      // Reset selected Variants, if results are updated
      state.selectedVariants = {};
    },
    setPagination(state, { payload }) {
      state.pagination = payload;
    },
    setPageNumber(state, { payload }) {
      if (state.pagination) {
        state.pagination.currentPage = payload;
      }
    },
    setSearchOptions(state, { payload }) {
      state.searchOptions = payload;
    },
    setIsSearching(state, { payload }) {
      state.isSearching = payload;
    },
    setFetching(state, { payload }) {
      state.isFetching = payload;
    },
    setIsGridMode(state, { payload }) {
      state.isGridMode = payload;
    },
    setXRequestId(state, { payload }) {
      state.xRequestId = payload;
    },
    setXSessionId(state, { payload }) {
      state.xSessionId = payload;
    },
    setCampaignBanners(state, { payload }) {
      state.campaignBanners = payload;
    },
    setMaterialsInQueue(state, { payload }) {
      state.materialsInQueue = payload;
    },
    setIsSearchQueryRelaxed(state, { payload }) {
      state.isSearchQueryRelaxed = payload;
    },
    setIsSearchRedirect(state, { payload }) {
      state.isSearchRedirect = payload;
    },
    setCollectionHeader(state, { payload }) {
      state.collectionHeader = payload;
    },
    setSelectedVariants(state, { payload }) {
      state.selectedVariants = { ...state.selectedVariants, ...payload };
    },
    setStripMaterials(state, { payload }) {
      state.stripMaterials = payload;
    },
    setIsStripMaterialsLoading(state, { payload }) {
      state.isStripMaterialsLoading = payload;
    },
    setContentBlocks(state, { payload }) {
      state.contentBlocks = payload;
    },
    setContentBlockXRequestId(state, { payload }) {
      state.contentBlockXRequestId = payload;
    },
    setProductWithFilePreviewInQueue(state, { payload }) {
      state.productWithFilePreviewInQueue = payload;
    },
  },
});

export const {
  setResults,
  setPagination,
  setPageNumber,
  setSearchOptions,
  setIsSearching,
  setFetching,
  setIsGridMode,
  setXRequestId,
  setXSessionId,
  setCampaignBanners,
  setMaterialsInQueue,
  setIsSearchQueryRelaxed,
  setIsSearchRedirect,
  setCollectionHeader,
  setSelectedVariants,
  setStripMaterials,
  setIsStripMaterialsLoading,
  setContentBlocks,
  setContentBlockXRequestId,
  setProductWithFilePreviewInQueue,
} = searchSlice.actions;

export default searchSlice.reducer;
